import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { createItem, listItems, listMoves } from "../api/acctIfrs";
import { AcctShell } from "../features/acct/AcctShell";
import { InvMoveForm } from "../features/acct/InvMoveForm";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";

export default function AcctInventoryPage() {
  const { t } = useTranslation();
  const { data, reload: load } = useCachedResource("acct-inventory", async () => ({
    items: await listItems(),
    moves: await listMoves(),
  }));
  const items = data?.items || [];
  const moves = data?.moves || [];
  const [name, setName] = useState("");

  const add = async () => {
    await createItem({ name });
    toast.success(t("acct.ifrs.itemCreated"));
    setName("");
    load();
  };

  return (
    <AcctShell titleKey="inventory">
      <NeuCard className="flex flex-wrap items-center gap-3 p-5" data-testid="item-form">
        <NeuInput className="w-56" data-testid="item-name" placeholder={t("acct.ifrs.itemName")} value={name} onChange={(e) => setName(e.target.value)} />
        <NeuButton variant="primary" data-testid="item-save" disabled={!name} onClick={add}>{t("acct.ifrs.addItem")}</NeuButton>
        <p className="text-[11px] text-muted-foreground">{t("acct.ifrs.inventoryHint")}</p>
      </NeuCard>

      <InvMoveForm items={items} onDone={load} />

      <div className="grid gap-2" data-testid="item-list">
        {items.map((item) => (
          <NeuCard key={item.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`item-row-${item.id}`}>
            <span className="min-w-0 flex-1 truncate text-sm font-bold">{item.name}</span>
            <span className="font-mono text-xs" dir="ltr">{item.quantity} {item.unit}</span>
            <span className="font-mono text-xs text-muted-foreground" dir="ltr">{item.unit_cost}</span>
            <span className="font-mono text-sm font-bold" dir="ltr">{item.value}</span>
          </NeuCard>
        ))}
      </div>

      <NeuCard className="p-5" data-testid="move-list">
        <p className="label-xs mb-3">{t("acct.ifrs.moves")}</p>
        {moves.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="move-empty">{t("acct.ifrs.noMoves")}</p>
        ) : null}
        <div className="grid gap-2">
          {moves.slice(0, 20).map((move) => (
            <div key={`${move.entry_number}-${move.item_id}`} className="flex flex-wrap items-center gap-3 rounded-xl px-3 py-2 shadow-neu-in">
              <span className="font-mono text-[11px]" dir="ltr">{move.date}</span>
              <span className="min-w-0 flex-1 truncate text-xs font-semibold">{move.item_name}</span>
              <span className={`text-[10px] font-bold ${move.kind === "in" ? "text-success" : "text-destructive"}`}>
                {t(`acct.ifrs.moveKind.${move.kind}`)}
              </span>
              <span className="font-mono text-xs" dir="ltr">{move.quantity}</span>
              <span className="font-mono text-xs font-bold" dir="ltr">{move.amount}</span>
              <span className="font-mono text-[11px] text-primary" dir="ltr">{move.entry_number}</span>
            </div>
          ))}
        </div>
      </NeuCard>
    </AcctShell>
  );
}
